import { array, object, string } from 'yup';
import { BiLogoBing, BiLogoGoogle, BiLogoYoutube } from 'react-icons/bi';
import type { IconType } from 'react-icons';
import type {
  CreateMassiveTaskRequest,
  MassiveHistoryItem,
  MassiveHistoryResponse,
  MassiveSource
} from '@/app/KeywordResearch/types/massiveApiTypes';

export const LIMIT_MASSIVE_KEYWORDS = 4;
export const LIMIT_FILTER_KEYWORDS = 20;
export const MAX_KEYWORD_RESULTS = 500;

export const MASSIVE_KEYWORD_COUNT_HINT =
  'برای دریافت نتایج دقیق‌تر و کاربردی‌تر، عبارت جستجوی خود را بین ۲ تا ۴ کلمه وارد کنید. عبارت‌های خیلی کوتاه یا خیلی طولانی ممکن است دقت نتایج را کاهش دهند.';

export const MASSIVE_HALF_SPACE_HINT =
  'کاربر عادی با نیم فاصله جستجو نمی‌کند!! پیشنهاد میکنیم اگر در کلمات نیم فاصله دارید حذف کنید.';

export const MASSIVE_LOW_RESULTS_THRESHOLD = 10;

export const MASSIVE_LOW_RESULTS_TIPS = [
  'عبارت‌های طولانی را کوتاه‌تر و عمومی‌تر وارد کنید.',
  'به‌جای یک عبارت، چند عبارت مرتبط وارد کنید.',
  'اگر فیلتر با AI فعال است، یک‌بار آن را غیرفعال کنید و با فیلتر دستیِ خالی دوباره جستجو کنید.'
] as const;

export type SelectOption = {
  value: string;
  label: string;
};

export type CountryOption = SelectOption & {
  flag?: string;
};

export type MassiveFormValues = {
  keyword: string[];
  include_keywords: string[];
  exclude_keywords: string[];
  include_operator: 'and' | 'or';
  exclude_operator: 'and' | 'or';
  smart_filter: boolean;
  filter_results: boolean;
  with_questions: boolean;
  sources: MassiveSource[];
};

export type MassiveNewFormProps = {
  isHistoryLoading: boolean;
  massiveHistory?: MassiveHistoryResponse;
};

export type SourceOption = {
  value: MassiveSource;
  icon: IconType;
  title: string;
  disabled?: boolean;
};

export type SubmitButtonState = {
  disabled: boolean;
  showLoading: boolean;
  tooltip: string;
};

export const MASSIVE_FORM_INITIAL_VALUES: MassiveFormValues = {
  keyword: [],
  include_keywords: [],
  exclude_keywords: [],
  include_operator: 'or',
  exclude_operator: 'or',
  smart_filter: true,
  filter_results: false,
  with_questions: false,
  sources: ['google']
};

export const SOURCE_OPTIONS: SourceOption[] = [
  { value: 'google', icon: BiLogoGoogle, title: 'Google', disabled: false },
  { value: 'youtube', icon: BiLogoYoutube, title: 'YouTube', disabled: true },
  { value: 'bing', icon: BiLogoBing, title: 'Bing',disabled: true  }
];

export const DEFAULT_COUNTRY: CountryOption = {
  value: 'ir',
  label: 'ایران',
  flag: 'https://static.mizfa.tools/flags/ir.webp'
};

export const DEFAULT_LANGUAGE: SelectOption = {
  value: 'fa',
  label: 'فارسی'
};

export const massiveFormValidationSchema = object().shape({
  keyword: array()
    .of(string())
    .test('not-empty', 'وارد کردن حداقل یک کلمه اجباری است.', v => {
      const count = (v || []).filter(w => w?.trim()).length;
      return count > 0;
    })
    .test(
      'max-keywords',
      'حداکثر ۴ کلمه می‌توانید وارد کنید.',
      v => (v || []).filter(w => w?.trim()).length <= LIMIT_MASSIVE_KEYWORDS
    ),
  include_keywords: array()
    .of(string())
    .max(LIMIT_FILTER_KEYWORDS, 'حداکثر ۲۰ کلمه.'),
  exclude_keywords: array()
    .of(string())
    .max(LIMIT_FILTER_KEYWORDS, 'حداکثر ۲۰ کلمه.'),
  sources: array()
    .min(1, 'حداقل یک منبع را انتخاب کنید.')
    .required()
});

export const parseKeywords = (lines: string[]) =>
  lines.filter(word => word !== '').map(word => word.trim());

export const countWordsInPhrase = (phrase: string): number =>
  phrase.trim().split(/\s+/).filter(Boolean).length;

/**
 * Soft hint when any phrase is longer than 4 words (including the current line),
 * or when a previous line is a single word after moving to the next line.
 */
export const shouldShowKeywordCountHint = (keywords: string[]): boolean => {
  const phrases = keywords.map(line => line.trim()).filter(Boolean);

  if (phrases.length === 0) return false;

  const hasOverLimitPhrase = phrases.some(
    phrase => countWordsInPhrase(phrase) > LIMIT_MASSIVE_KEYWORDS
  );
  if (hasOverLimitPhrase) return true;

  if (keywords.length <= 1) return false;

  const previousPhrases = keywords
    .slice(0, -1)
    .map(line => line.trim())
    .filter(Boolean);

  return previousPhrases.some(phrase => countWordsInPhrase(phrase) === 1);
};

export const hasHalfSpaceInKeywords = (keywords: string[]): boolean =>
  keywords.some(line => /\u200c/.test(line));

export const isMassiveOnProgress = (
  historyList?: MassiveHistoryItem[]
): boolean => {
  if (!historyList?.length) return false;
  return historyList.some(
    history => history.status === 4 || history.status === 2
  );
};

export const buildCreateTaskRequest = (
  values: MassiveFormValues,
  location: string,
  lang: string
): CreateMassiveTaskRequest => {
  const body: CreateMassiveTaskRequest = {
    with_alphabet: false,
    with_questions: values.with_questions,
    max_keyword: MAX_KEYWORD_RESULTS,
    sources: values.sources,
    location: location.toUpperCase(),
    lang,
    keywords: parseKeywords(values.keyword),
    ai_smart_filter: values.smart_filter
  };

  if (!values.smart_filter && values.filter_results) {
    const includes = parseKeywords(values.include_keywords);
    const excludes = parseKeywords(values.exclude_keywords);

    if (excludes.length) {
      body.keyword_filter = {
        mode: 'exclude',
        logic: values.exclude_operator,
        filter_terms: excludes
      };
    } else if (includes.length) {
      body.keyword_filter = {
        mode: 'include',
        logic: values.include_operator,
        filter_terms: includes
      };
    }
  }

  return body;
};

export const getKeywordDisplayError = (
  keywordCount: number,
  touched: boolean,
  error?: string | string[]
) => {
  if (keywordCount > LIMIT_MASSIVE_KEYWORDS) {
    return 'حداکثر ۴ کلمه می‌توانید وارد کنید.';
  }
  if (touched && error) {
    return Array.isArray(error) ? error[0] : error;
  }
  return null;
};

export const getSubmitButtonState = (params: {
  hasAccess: boolean;
  isLoading: boolean;
  isHistoryLoading: boolean;
  massiveHistory?: MassiveHistoryItem[];
  isKeywordEmpty: boolean;
  isKeywordOverLimit: boolean;
}): SubmitButtonState => {
  const massiveInProgress = isMassiveOnProgress(params.massiveHistory);
  const showLoading = params.isLoading || massiveInProgress;

  const disabled =
    !params.hasAccess ||
    params.isLoading ||
    params.isHistoryLoading ||
    massiveInProgress ||
    params.isKeywordEmpty ||
    params.isKeywordOverLimit;

  let tooltip = '';
  if (!showLoading) {
    if (!params.hasAccess) tooltip = 'دسترسی ندارید';
    else if (params.isKeywordEmpty) {
      tooltip = 'وارد کردن حداقل یک کلمه اجباری است.';
    } else if (params.isKeywordOverLimit) {
      tooltip = 'حداکثر ۴ کلمه می‌توانید وارد کنید.';
    }
  }

  return {
    disabled,
    showLoading,
    tooltip
  };
};

export const toggleSource = (
  currentSources: MassiveSource[],
  value: MassiveSource
): MassiveSource[] => {
  if (currentSources.includes(value)) {
    const next = currentSources.filter(item => item !== value);
    return next.length ? next : currentSources;
  }
  return [...currentSources, value];
};
